import type { Transaction } from 'dexie';

import { upgradeAircraftCategory } from './legacy';

/**
 * Migration discipline, in one file, from the first commit.
 *
 * The rules — enforced by `migrations.test.ts`, not by good intentions:
 *
 *  1. **A released migration is immutable.** Once a version has shipped, editing
 *     its `stores` or `upgrade` corrupts every database already on that version.
 *     Changing a schema means appending a new entry, always.
 *  2. **Versions are contiguous and ascending**, starting at 1.
 *  3. **`stores` is a delta**, not the whole schema. Dexie merges each version's
 *     declaration onto the previous one; list only what this version adds or
 *     changes, and `null` to drop a table.
 *  4. **An `upgrade` must be idempotent-safe and total.** It runs once per
 *     database, inside a transaction, and can see only tables that exist at that
 *     version. It must not read `Date.now()` in a way that changes meaning on
 *     replay, and must cope with rows written by older app builds.
 *
 * Adding a migration:
 *
 * ```ts
 * {
 *   version: 2,
 *   description: 'Add logbook entries',
 *   stores: { logbookEntries: 'id, flightId, date' },
 *   async upgrade(tx) { ... },
 * }
 * ```
 *
 * then add its fingerprint to the locked table in `migrations.test.ts`.
 */
export interface Migration {
  /** Dexie schema version. Contiguous from 1. */
  readonly version: number;
  /** Why this version exists — read by whoever debugs a failed upgrade. */
  readonly description: string;
  /**
   * Dexie store declarations added or changed at this version. `null` drops a
   * table. Empty for a version that only transforms data.
   */
  readonly stores: Readonly<Record<string, string | null>>;
  /** Data transformation applied when moving to this version. */
  readonly upgrade?: (tx: Transaction) => Promise<void> | void;
}

export const MIGRATIONS: readonly Migration[] = Object.freeze([
  {
    version: 1,
    description: 'Initial schema: tracks, columnar track points, flights, aircraft, settings',
    stores: {
      // `&contentHash` makes "no duplicate imports" a storage invariant rather
      // than a promise the import wizard has to keep.
      tracks: 'id, &contentHash, startTime, endTime, registration, source, importedAt',
      // Points are one fat row per track; nothing else is worth indexing.
      trackPoints: 'trackId',
      // `[aircraftId+date]` is the logbook's hot path: hours on type by period.
      flights: 'id, date, trackId, aircraftId, updatedAt, [aircraftId+date]',
      aircraft: 'id, &registration, icaoType, class',
      settings: 'key',
    },
  },
  {
    version: 2,
    description: 'Add logbook entries (CAR 61.29 field set)',
    stores: {
      // `[aircraftId+date]` mirrors flights' own index: it's the same
      // hours-on-type-by-period query, run against the logbook instead of the
      // track list.
      logbookEntries: 'id, flightId, date, aircraftId, confirmedAt, updatedAt, [aircraftId+date]',
    },
  },
  {
    version: 3,
    description:
      "Aircraft carry the CAA register's category; the single/multi-engine logbook split moves to engineCount",
    // The whole `aircraft` declaration, not an addition to it: `class` is gone
    // as a property, so its index has to go with it or Dexie keeps indexing a
    // field nothing writes any more.
    stores: { aircraft: 'id, &registration, icaoType, category' },
    async upgrade(tx) {
      /*
       * `class` → `category`, in the register's vocabulary.
       *
       * The conversion itself lives in `legacy.ts` because a restored backup
       * needs exactly the same one, and a second copy of it here is a second
       * copy to be wrong. What it does with a multi-engine record and with a
       * seaplane — the two old values that have no direct translation — is
       * documented there.
       *
       * Replay-safe: a row already converted has no `class` left to read and
       * is left exactly as it is.
       */
      await tx
        .table('aircraft')
        .toCollection()
        .modify((record: Record<string, unknown>) => {
          upgradeAircraftCategory(record);
        });
    },
  },
  {
    version: 4,
    description:
      'Add the formal checks a licence hangs on — flight tests, reviews, competency checks',
    stores: {
      // `date` to list them in order, `expiryDate` because the whole point
      // of the table is answering "what falls due next", and `flightId` for
      // the join back to the flight a check was flown on.
      logbookChecks: 'id, kind, date, expiryDate, flightId',
    },
  },
  {
    version: 5,
    description: 'Keep the original file each track was imported from',
    stores: {
      // `trackId` for the flight page and the cascade on delete; `sha256` so a
      // file dropped twice is recognised before it is stored twice. The bytes
      // themselves are not indexed — nothing is ever looked up by them.
      trackFiles: 'id, trackId, sha256',
    },
  },
  {
    version: 6,
    description: 'Remember deletions, so a sync folder can carry them to other devices',
    stores: {
      // `table` for listing one kind; `deletedAt` for the newest-first read
      // a merge wants. The key is `${table}:${id}`, so a record deleted twice
      // is one row.
      tombstones: 'key, table, deletedAt',
    },
  },
] satisfies readonly Migration[]);

/** The version a freshly opened database is upgraded to. */
export const SCHEMA_VERSION: number = MIGRATIONS[MIGRATIONS.length - 1]?.version ?? 0;

/**
 * A stable digest of a migration's observable schema effect. Two migrations with
 * the same fingerprint declare the same tables and indexes.
 *
 * `upgrade` is deliberately not part of it: a fingerprint guards the schema
 * shape, and JS function text is too brittle a thing to compare (minifiers,
 * formatters and TypeScript downlevelling all rewrite it).
 */
export function fingerprintMigration(migration: Migration): string {
  const stores = Object.keys(migration.stores)
    .sort()
    .map((table) => `${table}=${migration.stores[table] ?? '<dropped>'}`)
    .join(';');
  return `v${migration.version}:${stores}`;
}

/**
 * Structural checks on the migration list. Called on every database open so a
 * bad migration fails loudly at startup rather than halfway through an upgrade.
 *
 * @throws if the list violates rule 2 or is otherwise malformed.
 */
export function assertMigrationsAreWellFormed(migrations: readonly Migration[] = MIGRATIONS): void {
  if (migrations.length === 0) {
    throw new Error('No migrations declared: the database has no schema to open.');
  }

  migrations.forEach((migration, index) => {
    const expected = index + 1;
    if (migration.version !== expected) {
      throw new Error(
        `Migration at position ${index} declares version ${migration.version}, expected ${expected}. ` +
          'Versions must be contiguous and ascending from 1.',
      );
    }
    if (Object.keys(migration.stores).length === 0 && migration.upgrade === undefined) {
      throw new Error(
        `Migration v${migration.version} is a no-op: it declares no stores and no upgrade.`,
      );
    }
    if (migration.description.trim() === '') {
      throw new Error(`Migration v${migration.version} has no description.`);
    }
  });
}
